/**
 * Renew an expired sign-in and resend the request (ZLAYT-1649).
 *
 * Shared by the two axios instances (ApiHandler.ts and axios.ts) so both
 * recognise the same 401 bodies and both renew through the one shared
 * refresh (ZLAYT-1447): refresh tokens rotate and the old one is
 * blacklisted, so parallel renewals must share one request.
 */
import axios, { AxiosResponse } from 'axios';
import { isAccessTokenRefused } from './authErrors';
import { getStoredAccessToken, refreshAccessToken } from './refreshAccessToken';

/** The renewal itself failed: the caller signs the user out. */
export class SignInRenewalFailed extends Error {
  constructor(readonly cause: unknown) {
    super('Sign-in renewal failed');
  }
}

export const signOutLocally = () => {
  localStorage.setItem('accessToken', JSON.stringify(null));
  localStorage.setItem('refreshToken', JSON.stringify(null));
};

/**
 * For a 401 refused for its access token, renew (or reuse a token another
 * request already renewed) and resend once through plain axios, which has
 * no interceptors, so a resend can never renew again. Returns null when the
 * error is not such a 401 or the request was already resent; rejects with
 * SignInRenewalFailed when the renewal fails; otherwise returns the
 * resend, whose own failure (a 400, a 500, a 401) reaches the caller.
 */
export async function renewSignInAndResend(
  error: any,
  baseURL?: string
): Promise<AxiosResponse | null> {
  const originalRequest = error?.config;
  if (
    error?.response?.status !== 401 ||
    !originalRequest ||
    originalRequest._retry ||
    !isAccessTokenRefused(error.response.data)
  ) {
    return null;
  }
  originalRequest._retry = true;

  const sent = originalRequest.headers?.Authorization;
  let access = getStoredAccessToken();
  // Another request may have renewed while this one was in the air: resend
  // with the stored token instead of renewing again.
  if (!access || sent === `Bearer ${access}`) {
    try {
      access = await refreshAccessToken(baseURL);
    } catch (cause) {
      throw new SignInRenewalFailed(cause);
    }
  }

  originalRequest.headers.Authorization = `Bearer ${access}`;
  return axios(originalRequest);
}
